import { AuthService } from '../../../services/auth/auth.service';
import { inject, Component, OnInit } from '@angular/core';
import { blobImageUrl } from '../../../shared/item-images';
import {
  DerivativeBackfillResult,
  DerivativeScanItem,
  ImageDerivativesService
} from '../../../services/image-derivatives.service';

/** A scan hit plus its on-page lifecycle (queued → generating → outcome). */
interface WorkRow {
  item: DerivativeScanItem;
  generating: boolean;
  result?: DerivativeBackfillResult;
}

// Image Sizes console: images uploaded before the Inventory API produced resized copies
// (240/480/960) have none, so the POS grid downloads their full-size originals. Scan lists the
// container and shows every original still missing a copy or a Content-Type; Generate fixes one
// image, "Generate all" chunks through the whole backlog. A generated (or deliberately skipped —
// too small, gif/bmp) original is stamped server-side and drops out of the next scan.
@Component({
  selector: 'app-image-derivatives-page',
  standalone: false,
  templateUrl: './image-derivatives-page.component.html',
  styleUrls: ['./image-derivatives-page.component.css']
})
export class ImageDerivativesPageComponent implements OnInit {
  readonly staffAuth = inject(AuthService);
  /** The only containers product/category images live in. */
  readonly containers = ['items', 'categories'];
  container = 'items';

  rows: WorkRow[] = [];
  totalOriginals = 0;
  totalComplete = 0;
  truncated = false;
  loaded = false;
  scanning = false;

  /** True while "Generate all" walks the backlog; per-row buttons are disabled meanwhile. */
  runningAll = false;
  allProgress = 0;
  allTotal = 0;

  message = '';
  error = '';

  constructor(private derivatives: ImageDerivativesService) {}

  ngOnInit(): void {
    this.scan();
  }

  scan(): void {
    if (!this.staffAuth.can('Platform.Images.Generate')) return;
    this.scanning = true;
    this.error = '';
    this.message = '';
    this.derivatives.scan(this.container).subscribe({
      next: result => {
        this.rows = (result?.items ?? []).map(item => ({ item, generating: false }));
        this.totalOriginals = result?.totalOriginals ?? 0;
        this.totalComplete = result?.totalComplete ?? 0;
        this.truncated = result?.truncated ?? false;
        this.loaded = true;
        this.scanning = false;
      },
      error: () => {
        this.error = 'Scan failed. The Inventory API may be unreachable.';
        this.scanning = false;
      }
    });
  }

  onContainerChange(): void {
    this.rows = [];
    this.loaded = false;
    this.scan();
  }

  generate(row: WorkRow): void {
    if (!this.staffAuth.can('Platform.Images.Generate')) return;
    if (row.generating || this.runningAll) { return; }
    row.generating = true;
    this.error = '';
    this.derivatives.generate(this.container, [row.item.name]).subscribe({
      next: results => {
        row.result = results?.[0];
        row.generating = false;
      },
      error: err => {
        row.result = {
          name: row.item.name, status: 'failed', widthsWritten: [],
          contentTypeFixed: false, error: err?.message ?? 'Request failed'
        };
        row.generating = false;
      }
    });
  }

  /** Everything still without an outcome, in API-sized chunks, sequentially. */
  generateAll(): void {
    if (!this.staffAuth.can('Platform.Images.Generate')) return;
    const pending = this.rows.filter(r => !r.result && !r.generating);
    if (pending.length === 0 || this.runningAll) { return; }

    this.runningAll = true;
    this.allTotal = pending.length;
    this.allProgress = 0;
    this.error = '';
    this.message = '';
    this.runChunk(pending, 0);
  }

  private runChunk(pending: WorkRow[], offset: number): void {
    if (offset >= pending.length) {
      this.finishRun();
      return;
    }

    const chunk = pending.slice(offset, offset + this.derivatives.maxBatch);
    chunk.forEach(r => r.generating = true);

    this.derivatives.generate(this.container, chunk.map(r => r.item.name)).subscribe({
      next: results => {
        const byName = new Map((results ?? []).map(r => [r.name, r]));
        chunk.forEach(r => {
          r.result = byName.get(r.item.name)
            ?? { name: r.item.name, status: 'failed', widthsWritten: [], contentTypeFixed: false, error: 'No result returned' };
          r.generating = false;
        });
        this.allProgress += chunk.length;
        this.runChunk(pending, offset + chunk.length);
      },
      error: () => {
        chunk.forEach(r => r.generating = false);
        this.error = `Generation stopped after ${this.allProgress} of ${this.allTotal} — the request failed. Already-generated images keep their copies; run again to continue.`;
        this.runningAll = false;
      }
    });
  }

  private finishRun(): void {
    this.runningAll = false;
    const generated = this.rows.filter(r => r.result?.status === 'generated').length;
    const failed = this.rows.filter(r => r.result?.status === 'failed' || r.result?.status === 'notFound').length;
    const skipped = this.rows.filter(r => r.result && ['tooSmall', 'notResizable', 'alreadyComplete'].includes(r.result.status)).length;
    this.message = `Done: ${generated} generated, ${skipped} needed nothing, ${failed} failed.`
      + (this.truncated ? ' The scan was truncated — scan again for the rest of the backlog.' : '');
  }

  get pendingCount(): number {
    return this.rows.filter(r => !r.result).length;
  }

  thumbUrl(name: string): string {
    return blobImageUrl(this.container, name);
  }

  sizeLabel(bytes: number | null): string {
    if (bytes === null || bytes === undefined) { return ''; }
    if (bytes >= 1024 * 1024) { return (bytes / (1024 * 1024)).toFixed(1) + ' MB'; }
    return Math.max(1, Math.round(bytes / 1024)) + ' KB';
  }

  statusLabel(result: DerivativeBackfillResult): string {
    switch (result.status) {
      case 'generated': {
        const widths = result.widthsWritten.map(w => `_${w}`).join(', ');
        return `Generated ${widths}` + (result.contentTypeFixed ? ' · type fixed' : '');
      }
      case 'tooSmall': return 'Too small for any copy' + (result.contentTypeFixed ? ' · type fixed' : '');
      case 'notResizable': return 'Not resizable (gif/bmp/unreadable)' + (result.contentTypeFixed ? ' · type fixed' : '');
      case 'alreadyComplete': return 'Already complete';
      case 'notFound': return 'Blob no longer exists';
      default: return 'Failed' + (result.error ? `: ${result.error}` : '');
    }
  }

  isOk(result: DerivativeBackfillResult): boolean {
    return result.status !== 'failed' && result.status !== 'notFound';
  }
}
